import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { getProfile } from "@/api/profile";
import { useAuthStore } from "@/stores/auth";
import { SUPPORTED_LANGUAGES, type AppLanguage } from "@/i18n";

const PUBLIC_AUTH_ROUTES = new Set(["/login", "/register", "/forgot-password", "/reset-password"]);

/**
 * Keeps i18n.language in sync with the authenticated user's saved language.
 *
 * Runs on account pages when a token is present. Public auth pages own their
 * language through their arriving URL and picker, even if a previous account's
 * token or in-flight profile request still exists. Pulls the profile,
 * reads user.language, and calls i18n.changeLanguage() if it differs from
 * whatever the detector picked up (localStorage or browser). Also writes
 * localStorage so future sessions on this device default to the right
 * language even before the profile fetch completes.
 */
export function LanguageSync() {
  const token = useAuthStore((s) => s.token);
  const { pathname } = useLocation();
  const publicAuthPage = PUBLIC_AUTH_ROUTES.has(pathname.replace(/\/+$/, ""));
  const { i18n } = useTranslation();
  const profileQ = useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
    enabled: !!token && !publicAuthPage,
    staleTime: 60_000,
  });

  useEffect(() => {
    // Disabling the query does not discard cached or already in-flight data.
    // Guard the effect too, then resume the saved locale on account navigation.
    if (!token || publicAuthPage) return;
    const serverLang = profileQ.data?.language as AppLanguage | undefined;
    if (!serverLang) return;
    if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(serverLang)) return;
    if (serverLang === i18n.language) return;
    i18n.changeLanguage(serverLang);
    localStorage.setItem("mavinty_language", serverLang);
  }, [profileQ.data?.language, i18n, token, publicAuthPage]);

  return null;
}
